import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import {
  useForm,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type {
  ForeignNational,
  UpdateForeignNationalRequest
} from "../../../../api/types/foreignNational";
import {
  buildPersonalPayload,
  personalSchema,
  valuesFromClient,
  type PersonalValues
} from "./personalModel";

export type PersonalForm = {
  form: UseFormReturn<PersonalValues>;
  /** "•••-••-1234" for the stored SSN, or null when none is on file. */
  ssnMasked: string | null;
  /** Whether the full SSN is in the form (after a reveal or while typing one). */
  ssnRevealed: boolean;
  /** Fetches the full SSN (logged in the client's activity) into the form. */
  revealSsn: () => Promise<void>;
  isRevealingSsn: boolean;
  isDirty: boolean;
  isSaving: boolean;
  setMailingSameAsPhysical: (same: boolean) => void;
  /** Persists validated values (wire to `<Form onSubmit>`). */
  submit: SubmitHandler<PersonalValues>;
  /** Validates, then persists. */
  save: () => Promise<void>;
  /** Drops unsaved edits. */
  reset: () => void;
};

type Updater = (
  id: string,
  payload: UpdateForeignNationalRequest
) => Promise<ForeignNational>;

/**
 * react-hook-form state for the Personal Information form. The form's
 * default values are the last persisted snapshot, so dirty tracking survives
 * a save without a refetch.
 */
export const usePersonalForm = (
  clientId: string,
  client: ForeignNational | null,
  updateForeignNational: Updater
): PersonalForm => {
  const form = useForm<PersonalValues>({
    resolver: zodResolver(personalSchema),
    defaultValues: valuesFromClient(client)
  });
  const [isSaving, setIsSaving] = useState(false);
  const [ssnRevealed, setSsnRevealed] = useState(false);
  const [isRevealingSsn, setIsRevealingSsn] = useState(false);

  useEffect(() => setSsnRevealed(false), [clientId]);

  const revealSsn = useCallback(async () => {
    if (!clientId || isRevealingSsn) return;
    setIsRevealingSsn(true);
    try {
      const ssn = await ForeignNationalsAPI.revealSsn(clientId);
      form.resetField("ssn", { defaultValue: ssn ?? "" });
      setSsnRevealed(true);
    } catch (e) {
      toast.error("Couldn't show the SSN", {
        description: e instanceof Error ? e.message : "Please try again."
      });
    } finally {
      setIsRevealingSsn(false);
    }
  }, [clientId, form, isRevealingSsn]);

  // Re-seed whenever the client record changes underneath us, keeping any
  // edits in progress.
  useEffect(() => {
    form.reset(valuesFromClient(client), { keepDirtyValues: true });
  }, [client, form]);

  const setMailingSameAsPhysical = useCallback(
    (same: boolean) => {
      form.setValue("mailingSameAsPhysical", same, { shouldDirty: true });
      // Switching to "different" starts from the physical address so the
      // user edits rather than retypes.
      if (!same) {
        form.setValue("mailing", form.getValues("physical"), {
          shouldDirty: true
        });
      }
    },
    [form]
  );

  const reset = useCallback(() => {
    form.reset();
    setSsnRevealed(false);
  }, [form]);

  const submit = useCallback<SubmitHandler<PersonalValues>>(
    async (values) => {
      if (!clientId || !client || isSaving) return;
      setIsSaving(true);
      try {
        const payload = buildPersonalPayload(values);
        if (form.getFieldState("ssn").isDirty) {
          payload.ssn = values.ssn.trim() || null;
        }
        const updated = await updateForeignNational(clientId, payload);
        form.reset(valuesFromClient(updated));
        setSsnRevealed(false);
        toast.success("Personal information updated", {
          description: "Your changes have been saved to this client profile."
        });
      } catch (e) {
        toast.error("Couldn't save changes", {
          description:
            e instanceof Error ? e.message : "Please try again in a moment."
        });
      } finally {
        setIsSaving(false);
      }
    },
    [client, clientId, form, isSaving, updateForeignNational]
  );

  const save = useCallback(async () => {
    await form.handleSubmit(submit)();
  }, [form, submit]);

  return {
    form,
    ssnMasked: client?.ssn_masked ?? null,
    ssnRevealed,
    revealSsn,
    isRevealingSsn,
    isDirty: form.formState.isDirty,
    isSaving,
    setMailingSameAsPhysical,
    submit,
    save,
    reset
  };
};
